import React, { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * What an activity actually says, shown on hover.
 *
 * A DELIBERATE COPY of scale-portal's ActivityHoverCard — same two pieces of
 * content, same geometry, same portal. Kept in step by hand; the two apps share
 * no package.
 *
 * WHY THIS REPLACED THE `title` ATTRIBUTE
 * ---------------------------------------
 * Adam, 10 Oct, twice: "the hover doesn't work well here, you don't really see
 * what's going on, so that needs to be cleaned up", and "I think we have to do
 * a better job showing the notes — how does somebody see the notes associated
 * with this?"
 *
 * The chip was using a native `title`, which the browser renders as a grey
 * system tooltip after about a second, with no styling, no line breaks that can
 * be relied on, and a hard length limit. The notes a mediator types are the
 * whole reason they recorded the activity, and they were effectively invisible.
 *
 * TWO THINGS ONLY: the full title and the notes. The first version also
 * carried a labelled When / Length / Method table and a "No notes" line, which
 * restated what the chip, the cell and the form already show and made the card
 * read as filler. The notes are the one thing invisible everywhere else, and
 * they are what Adam asked for.
 *
 * It is NOT clickable — hovering is for reading, clicking is for acting, and a
 * hover target you have to travel into is a trap on a dense calendar grid.
 *
 * Rendered through a portal for the same reason the actions menu is: a day cell
 * clips its children, and on the classic board FullCalendar's grid clips them
 * again. Positioned from the chip's rect so it behaves the same in both.
 */

const CARD_WIDTH = 260
const GAP = 8

const ActivityHoverCard = ({ activity, anchorRef, open }) => {
    const [pos, setPos] = useState(null)
    const cardRef = useRef(null)

    useLayoutEffect(() => {
        if (!open || !anchorRef?.current) {
            setPos(null)
            return
        }
        const r = anchorRef.current.getBoundingClientRect()
        const h = cardRef.current?.offsetHeight || 120
        /* Prefer below; flip above when the last week of the month would push
         * the card off the bottom. Clamped horizontally so a Friday column does
         * not run off the right edge. */
        const below = r.bottom + GAP
        const flip = below + h > window.innerHeight - GAP
        setPos({
            top: flip ? Math.max(GAP, r.top - GAP - h) : below,
            left: Math.max(
                GAP,
                Math.min(r.left, window.innerWidth - CARD_WIDTH - GAP)
            ),
        })
    }, [open, anchorRef, activity])

    if (!open || !activity) return null

    return createPortal(
        <div
            ref={cardRef}
            style={{
                position: 'fixed',
                top: pos ? `${pos.top}px` : '-9999px',
                left: pos ? `${pos.left}px` : '-9999px',
                maxWidth: `${CARD_WIDTH}px`,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 10px 28px rgba(15,42,40,.16)',
                padding: '9px 11px',
                zIndex: 10040,
                /* Never intercept the pointer: the chip underneath owns the
                 * click, and a card that swallowed it would make the chip feel
                 * broken the moment it appeared. */
                pointerEvents: 'none',
                fontFamily: 'inherit',
            }}
        >
            {/* The FULL title — the chip shows the abbreviated one and
                ellipsises it, so this is where a mediator finds out what
                "Pre-Med - Testing-lm-…" actually was. */}
            <div
                style={{
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: '#111827',
                    lineHeight: 1.4,
                }}
            >
                {activity.display_title}
            </div>

            {/* Notes, and nothing else.
                The card used to carry a When/Length/Method table and a "No
                notes" placeholder — three labelled rows restating what the chip
                and the form already show, which is what made it read as
                filler. The date and time are on the chip, in the cell, and in
                the form; only the notes are invisible everywhere else, and they
                are the reason Adam asked for this: "how does somebody see the
                notes associated with this?"

                No notes means no second line. An empty card saying "No notes"
                is a card that exists to say nothing. */}
            {activity.notes ? (
                <div
                    style={{
                        marginTop: '5px',
                        fontSize: '12px',
                        lineHeight: 1.5,
                        color: '#6b7280',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '110px',
                        overflow: 'hidden',
                    }}
                >
                    {activity.notes}
                </div>
            ) : null}
        </div>,
        document.body
    )
}

export default ActivityHoverCard
